import { useState } from 'react'
import { useChatStore } from '../../stores/chatStore'
import Button from '../ui/Button'
import Input from '../ui/Input'
import SearchableSelect from '../ui/SearchableSelect'

interface SupportTicketFormProps {
  onClose: () => void
}

export default function SupportTicketForm({ onClose }: SupportTicketFormProps) {
  const { addMessage } = useChatStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'normal' as 'low' | 'normal' | 'high',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.subject.trim() || !formData.description.trim()) return

    setIsSubmitting(true)

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))

    // Generate ticket ID
    const ticketId = `TKT-${Math.random().toString(36).substr(2, 6).toUpperCase()}`

    addMessage({
      role: 'system',
      content: `Support ticket ${ticketId} created`,
    })

    addMessage({
      role: 'bot',
      content: `Your support ticket **${ticketId}** has been created successfully! 🎫\n\n**Subject:** ${formData.subject}\n**Priority:** ${formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)}\n\nOur team will review your request and get back to you within 24 hours. You'll receive updates via email.`,
    })

    setIsSubmitting(false)
    onClose()
  }

  return (
    <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-900 dark:text-white">Create Support Ticket</h4>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <i className="fas fa-times"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          label="Subject"
          value={formData.subject}
          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          placeholder="Brief description of your issue"
          required
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Please provide details about your issue..."
            rows={3}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-sm"
          />
        </div>

        <SearchableSelect
          label="Priority"
          value={formData.priority}
          onChange={(val) => setFormData({ ...formData, priority: val as 'low' | 'normal' | 'high' })}
          searchable={false}
          options={[
            { value: 'low', label: 'Low - General inquiry' },
            { value: 'normal', label: 'Normal - Need help soon' },
            { value: 'high', label: 'High - Urgent issue' },
          ]}
        />

        <div className="flex gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            className="flex-1"
          >
            <i className="fas fa-paper-plane mr-2"></i>
            Submit
          </Button>
        </div>
      </form>
    </div>
  )
}
